import { useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { uploadsService } from '@/shared/api/services/uploads.service';
import { anglesService } from '@/shared/api/services/angles.service';
import { ApiClientError } from '@/shared/lib/api';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import {
  projectAngleKeys,
  useProjectAngles,
} from '@/shared/api/hooks/use-project-angles';
import { wsLogger } from '@/shared/lib/logger';
import {
  isProjectAngleValidating,
  type ProjectAngle,
} from '@/types/project-angle';
import {
  VALIDATION_POLL_INTERVAL_MS,
  VALIDATION_WAIT_TIMEOUT_MS,
} from '@/features/angle/lib/validation-polling';
import {
  carriedWaitMs,
  planLibraryWatch,
  withStalledViews,
  type StalledView,
} from '@/features/angle/lib/library-validation-watch';

/**
 * L1 for a building: wait out the intakes that are still running when the page
 * opens, then reconcile what they made.
 *
 * The upload window watches its own batch, but it dies with the window — and
 * with the tab. Everything an intake produced (a view, a reference, the
 * server-computed rescue flag) lands on the building's own rows, so one refresh
 * at the end is the whole recovery; no per-file UI is rebuilt.
 *
 * Same cadence and the same single wait budget as the window's poll: every
 * round spends it, a failed one too. A library tile still validating when the
 * budget runs out would spin with nothing to read (and keep Create design
 * disabled), so it is shown as a technical error carrying the last API refusal
 * or the timeout line, and polling stops. The error is provisional: it lives
 * only in the query cache and is kept across refetches that return the same
 * row version. A new version of the row (socket verdict, or a refresh with a
 * different `updated_at`) or a new validating row brings the server back in
 * charge, with a fresh budget.
 */
export function useUnfinishedIntakes(projectId: string | undefined): void {
  const queryClient = useQueryClient();
  const t = useTranslations('angle.upload.modal');
  // Read, never re-subscribed on — a locale switch must not restart the wait.
  const timeoutMessageRef = useRef(t('validationTimeout'));
  timeoutMessageRef.current = t('validationTimeout');

  // Rows given up on, by the server version they were given up at.
  const stalledRef = useRef(new Map<string, StalledView>());
  // Budget spent by the current watch, carried across restarts that only
  // drop rows, so a verdict for one view does not refill the others' wait.
  const watchRef = useRef({ ids: [] as string[], waitedMs: 0 });
  // The building whose mount-time intake watch has ended (settled or out of
  // budget). Only a validating row starts another one after that.
  const watchEndedForRef = useRef<string | null>(null);

  const { data: angles } = useProjectAngles(projectId ?? '');
  const plan = useMemo(
    () => planLibraryWatch(angles ?? [], stalledRef.current),
    [angles]
  );
  const watchKey = plan.watchIds.join(',');

  // Keep given-up rows shown as the error when a refetch returns them
  // unchanged; forget them once the server has moved on.
  useEffect(() => {
    if (!projectId) return;
    for (const id of plan.released) stalledRef.current.delete(id);
    if (plan.restall.size === 0) return;
    queryClient.setQueryData<ProjectAngle[]>(
      projectAngleKeys.list(projectId),
      (rows) => (rows ? withStalledViews(rows, plan.restall) : rows)
    );
  }, [projectId, plan, queryClient]);

  useEffect(() => {
    if (!projectId) return;
    if (!watchKey && watchEndedForRef.current === projectId) return;

    const ids = watchKey ? watchKey.split(',') : [];
    let waitedMs = carriedWaitMs(
      watchRef.current.ids,
      watchRef.current.waitedMs,
      ids
    );
    let cancelled = false;

    const endWatch = () => {
      watchEndedForRef.current = projectId;
      watchRef.current = { ids: [], waitedMs: 0 };
    };

    const refreshBuilding = () => {
      queryClient.invalidateQueries({
        queryKey: projectAngleKeys.list(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.references.listsByProject(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.projects.detail(projectId),
      });
    };

    const giveUp = async (message: string) => {
      // Read the rows fresh so a verdict that landed meanwhile is not
      // overwritten; the cached list is the fallback when the API is down.
      let rows: ProjectAngle[] | undefined;
      try {
        rows = await anglesService.listProjectAngles(projectId);
      } catch (error) {
        wsLogger.warn('[INTAKE] angle list failed after the wait:', error);
        rows = queryClient.getQueryData<ProjectAngle[]>(
          projectAngleKeys.list(projectId)
        );
      }
      if (cancelled) return;
      endWatch();
      if (!rows) return;

      const watched = new Set(ids);
      const messages = new Map<string, string>();
      for (const angle of rows) {
        if (watched.has(angle.id) && isProjectAngleValidating(angle)) {
          stalledRef.current.set(angle.id, {
            updatedAt: angle.attributes.updated_at,
            message,
          });
          messages.set(angle.id, message);
        }
      }
      if (messages.size > 0) {
        wsLogger.warn(
          `[INTAKE] ${messages.size} view(s) of ${projectId} still validating after the wait budget`
        );
      }
      queryClient.setQueryData<ProjectAngle[]>(
        projectAngleKeys.list(projectId),
        withStalledViews(rows, messages)
      );
      queryClient.invalidateQueries({
        queryKey: cacheKeys.references.listsByProject(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.projects.detail(projectId),
      });
    };

    const run = async () => {
      let sawAny = false;
      let lastRejection: string | undefined;

      for (;;) {
        try {
          const pending = await uploadsService.listPending(projectId);
          if (cancelled) return;
          lastRejection = undefined;
          if (pending.length === 0) {
            endWatch();
            if (sawAny || ids.length > 0) {
              wsLogger.info(`[INTAKE] unfinished intakes of ${projectId} settled`);
              refreshBuilding();
            }
            return;
          }
          sawAny = true;
        } catch (error) {
          if (cancelled) return;
          // A failed round spends the budget too: returning here left a
          // validating tile spinning with nobody asking again.
          wsLogger.warn('[INTAKE] pending list failed:', error);
          lastRejection =
            error instanceof ApiClientError ? error.message : undefined;
        }

        waitedMs += VALIDATION_POLL_INTERVAL_MS;
        watchRef.current = { ids, waitedMs };
        if (waitedMs >= VALIDATION_WAIT_TIMEOUT_MS) break;

        await new Promise((resolve) =>
          setTimeout(resolve, VALIDATION_POLL_INTERVAL_MS)
        );
        if (cancelled) return;
      }

      await giveUp(lastRejection || timeoutMessageRef.current);
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [projectId, watchKey, queryClient]);
}
